/**
 * Enroll endpoints, in Settings' Endpoints group: the ingredients of
 * enrolling an endpoint (an enrollment token, the installer, the command that
 * runs it with the token) as numbered steps, then, when the page is given
 * them, the check-ins: the live strip that listens for the first check-in and
 * then the first AI activity.
 *
 * Every step is permission-adaptive rather than permission-gated: a reader who
 * can't mint still sees the steps and a paste field; a role that can't list
 * installers is told to ask for the file.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { restGet, restPost } from "../lib/restClient.js";
import { query } from "../lib/dataClient.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { denialOrRawMessage } from "../lib/denialReason.js";
import {
    PLATFORM_LABEL,
    collapseUniversalRows,
    flattenInstaller,
    latestPerEnvironment,
} from "./InstallersPage.jsx";
import { markTenantConnected, useConnectState } from "../lib/connectState.js";
import {
    detectInstallPlatform,
    installCommandFor,
    installerPlatforms,
    looksLikeJwt,
    pickInstaller,
} from "../lib/connectCommands.js";
import { flattenInventoryRow } from "../lib/inventoryRows.js";
import Button from "../components/ui/Button.jsx";
import { CopyBlock, CopyField } from "../components/ui/Copy.jsx";
import Field from "../components/ui/Field.jsx";
import Notice, { ErrorText } from "../components/ui/Notice.jsx";
import Select from "../components/ui/Select.jsx";
import { SkeletonBar } from "../components/ui/Skeleton.jsx";
import { Step, Steps } from "../components/ui/Steps.jsx";
import { FIELD } from "../components/ui/styles.js";
import { timeLabelFormatter, dayLabelFormatter } from "../lib/traceUtils.js";
import { STATIC_MDM_PROFILES, downloadTextFile } from "../lib/mdmProfiles.js";

const CHECKIN_POLL_MS = 5000;
// Once the fleet exists the strip is a watch, not a wait — poll gently.
const CHECKIN_POLL_SLOW_MS = 15000;
const ACTIVITY_POLL_MS = 8000;
const RECENT_TOTAL_REFRESH_MS = 60000;
const RECENT_LIMIT = 25;

// Mint defaults mirror ProvisioningTokensPage (7 days / 10 uses). The custom
// option below keeps the quick path compact while allowing less common limits.
const DEFAULT_MINT_DURATION_DAYS = 7;
const DEFAULT_MINT_MAX_USES = 10;
const MAX_MINT_DURATION_DAYS = Math.floor(0xffffffff / 24);
const MAX_MINT_USES = 100000;
const MINT_DURATION_OPTIONS = [1, 7, 14, 30, 90, 365];
const MINT_MAX_USES_OPTIONS = [1, 10, 25, 50, 100, 500];
const CUSTOM_MINT_VALUE = "custom";

const INSTALLER_FORMAT_ORDER = [
    "INSTALLER_FORMAT_MSI",
    "INSTALLER_FORMAT_EXE",
    "INSTALLER_FORMAT_PKG",
];

export function installerChoicesForRows(rows) {
    const platforms = installerPlatforms(rows);
    const sortFormats = (formats) => formats.sort((a, b) => {
        const ai = INSTALLER_FORMAT_ORDER.indexOf(a);
        const bi = INSTALLER_FORMAT_ORDER.indexOf(b);
        return (ai === -1 ? INSTALLER_FORMAT_ORDER.length : ai) - (bi === -1 ? INSTALLER_FORMAT_ORDER.length : bi)
            || a.localeCompare(b);
    });
    const platformLabels = {
        PLATFORM_WINDOWS: "Windows",
        PLATFORM_MACOS: "macOS",
        PLATFORM_LINUX: "Linux",
    };
    return platforms.flatMap((platform) => sortFormats([...new Set(rows
        .filter((row) => row.os === platform)
        .map((row) => row.installerFormat)
        .filter(Boolean))])
        .map((format) => ({
            key: platform + "-" + format,
            label: (platformLabels[platform] ?? platform) + " " + format.replace("INSTALLER_FORMAT_", "") + " Installer",
            platform,
            format,
        })));
}

// The minted/pasted JWT is held for the SESSION, in module scope like the
// auth token store — never persisted. Navigating away and back keeps the
// command block populated without re-minting (the gateway only returns the
// JWT once).
let _sessionJwt = null;

// A step's explanation or a quiet status line.
const NOTE = "m-0 text-[12.5px] leading-[1.45] text-[color:var(--muted)]";
// The live strip's state, in a line.
const STATUS = "m-0 text-[13px] font-medium leading-[1.3] text-[color:var(--ink)]";

function ShimmerBar() {
    return <div aria-hidden="true" className="connect-shimmer h-[3px] w-full rounded-full" />;
}

function positiveInteger(value, maximum = Number.MAX_SAFE_INTEGER) {
    const normalized = String(value ?? "").trim();
    if (!/^\d+$/.test(normalized)) return null;
    const parsed = Number(normalized);
    return Number.isSafeInteger(parsed) && parsed >= 1 && parsed <= maximum ? parsed : null;
}

function positiveIntegerInput(value) {
    return String(value ?? "").replace(/\D/g, "").replace(/^0+/, "");
}

// ── Step 1: token ────────────────────────────────────────────────

export function TokenStep({ jwt, onJwt, canManage, caps }) {
    const [minting, setMinting] = useState(false);
    const [mintError, setMintError] = useState(null);
    const [paste, setPaste] = useState("");
    const [durationDays, setDurationDays] = useState(String(DEFAULT_MINT_DURATION_DAYS));
    const [customDurationDays, setCustomDurationDays] = useState("");
    const [maxUses, setMaxUses] = useState(String(DEFAULT_MINT_MAX_USES));
    const [customMaxUses, setCustomMaxUses] = useState("");

    const mint = useCallback(async () => {
        const selectedDurationDays = positiveInteger(
            durationDays === CUSTOM_MINT_VALUE ? customDurationDays : durationDays,
            MAX_MINT_DURATION_DAYS,
        );
        const selectedMaxUses = positiveInteger(
            maxUses === CUSTOM_MINT_VALUE ? customMaxUses : maxUses,
            MAX_MINT_USES,
        );
        if (selectedDurationDays === null) {
            setMintError("Enter a whole number of days greater than zero.");
            return;
        }
        if (selectedMaxUses === null) {
            setMintError("Enter a whole number of uses greater than zero.");
            return;
        }

        setMinting(true);
        setMintError(null);
        try {
            const resp = await restPost("/v1/provisioning-tokens", {
                duration_hours: selectedDurationDays * 24,
                max_uses: selectedMaxUses,
            });
            const token = resp?.jwt ?? "";
            if (!token) throw new Error("The gateway returned no token.");
            onJwt(token);
        } catch (err) {
            setMintError(denialOrRawMessage(err, caps));
        } finally {
            setMinting(false);
        }
    }, [caps, customDurationDays, customMaxUses, durationDays, maxUses, onJwt]);

    if (jwt) {
        return <CopyField label="Enrollment token" value={jwt} className="max-w-[640px]" />;
    }

    if (canManage) {
        return (
            <>
                <div className="grid max-w-[420px] items-start gap-[12px] [grid-template-columns:minmax(0,1fr)_minmax(0,1fr)]">
                    <Field label="Valid for">
                        <Select
                            ariaLabel="Valid for"
                            value={durationDays}
                            onChange={setDurationDays}
                            options={[
                                ...MINT_DURATION_OPTIONS.map((days) => ({ value: String(days), label: `${days} ${days === 1 ? "day" : "days"}` })),
                                { value: CUSTOM_MINT_VALUE, label: "Custom" },
                            ]}
                        />
                        {durationDays === CUSTOM_MINT_VALUE ? (
                            <input
                                type="number"
                                min="1"
                                max={MAX_MINT_DURATION_DAYS}
                                step="1"
                                inputMode="numeric"
                                aria-label="Custom days valid"
                                placeholder="Enter days"
                                value={customDurationDays}
                                onChange={(e) => setCustomDurationDays(positiveIntegerInput(e.target.value))}
                                onKeyDown={(e) => {
                                    if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
                                }}
                                className={FIELD}
                            />
                        ) : null}
                    </Field>
                    <Field label="Endpoints it can enroll">
                        <Select
                            ariaLabel="Endpoints it can enroll"
                            value={maxUses}
                            onChange={setMaxUses}
                            options={[
                                ...MINT_MAX_USES_OPTIONS.map((uses) => ({ value: String(uses), label: String(uses) })),
                                { value: CUSTOM_MINT_VALUE, label: "Custom" },
                            ]}
                        />
                        {maxUses === CUSTOM_MINT_VALUE ? (
                            <input
                                type="number"
                                min="1"
                                step="1"
                                inputMode="numeric"
                                aria-label="Custom number of endpoints"
                                max={MAX_MINT_USES}
                                placeholder="Enter endpoints"
                                value={customMaxUses}
                                onChange={(e) => setCustomMaxUses(positiveIntegerInput(e.target.value))}
                                onKeyDown={(e) => {
                                    if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
                                }}
                                className={FIELD}
                            />
                        ) : null}
                    </Field>
                </div>
                <div className="flex flex-wrap items-center gap-[12px]">
                    <Button variant="primary" onClick={mint} disabled={minting}>
                        {minting ? "Generating…" : "Generate token"}
                    </Button>
                    <ErrorText>{mintError}</ErrorText>
                </div>
            </>
        );
    }

    const pasteValid = looksLikeJwt(paste);
    return (
        <>
            <p className={NOTE}>
                Your role can't generate enrollment tokens. Ask an administrator for one, then paste it here.
            </p>
            <div className="flex max-w-[640px] gap-[8px]">
                <input
                    type="text"
                    aria-label="Enrollment token"
                    value={paste}
                    onChange={(e) => setPaste(e.target.value)}
                    placeholder="eyJ… enrollment token"
                    spellCheck={false}
                    className={`${FIELD} min-w-0 flex-1 font-mono text-[12px]`}
                />
                <Button onClick={() => pasteValid && onJwt(paste.trim())} disabled={!pasteValid}>
                    Use
                </Button>
            </div>
        </>
    );
}

// ── Step 2: installer ────────────────────────────────────────────

function formatBytesShort(n) {
    if (!Number.isFinite(n) || n <= 0) return null;
    const units = ["B", "KB", "MB", "GB"];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
    return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function InstallerStep({ installers, choiceKey, onChoice, choices, chosen }) {
    const [dlState, setDlState] = useState(null); // null | "pending" | error string

    const download = useCallback(async () => {
        if (!chosen) return;
        setDlState("pending");
        try {
            const resp = await restPost("/v1/installers:download-url", {
                version: chosen.version,
                os: chosen.os,
                architecture: chosen.architecture,
                installer_format: chosen.installerFormat,
            });
            const url = resp?.downloadUrl ?? resp?.download_url ?? "";
            if (!url) throw new Error("Empty download_url in response");
            window.open(url, "_blank", "noopener,noreferrer");
            setDlState(null);
        } catch (err) {
            setDlState(err.message || String(err));
        }
    }, [chosen]);

    if (installers.status === "loading") {
        return (
            <div aria-busy="true" className="flex h-[32px] w-[280px] max-w-full items-center rounded-[7px] border border-[color:var(--line-soft)] px-[10px]">
                <span className="sr-only">Loading installers…</span>
                <SkeletonBar className="w-[60%]" />
            </div>
        );
    }
    if (installers.status === "denied") {
        return (
            <p className={NOTE}>
                Your role can't list installers. Ask an administrator for the installer for this machine; the command in step 3 still applies.
            </p>
        );
    }
    if (installers.status === "error") {
        return <ErrorText>Couldn't load the installer catalog. Reload the page to try again.</ErrorText>;
    }
    if (!installers.rows.length) {
        return <p className={NOTE}>No installers are published yet.</p>;
    }

    const size = chosen ? formatBytesShort(chosen.sizeBytes) : null;
    return (
        <>
            <div className="flex flex-wrap items-center gap-[10px]">
                <div className="w-[280px] max-w-full">
                    <Select
                        ariaLabel="Choose an installer"
                        value={choiceKey || ""}
                        onChange={(key) => onChoice(key || null)}
                        placeholder="Choose an installer"
                        options={choices.map((choice) => ({
                            value: choice.key,
                            label: `${choice.label}${choice.installer ? ` - v${choice.installer.version}` : " — unavailable"}`,
                            disabled: !choice.installer,
                        }))}
                    />
                </div>
                {chosen ? (
                    <>
                        <Button onClick={download} disabled={dlState === "pending"}>
                            {dlState === "pending" ? "Generating link…" : "Download"}
                        </Button>
                        {size ? <span className="text-[12.5px] tabular-nums text-[color:var(--muted)]">{size}</span> : null}
                    </>
                ) : null}
            </div>
            <ErrorText>{typeof dlState === "string" && dlState !== "pending" ? dlState : null}</ErrorText>
            {chosen?.downloadSha256 ? (
                <span className="truncate font-mono text-[11.5px] leading-[1.4] text-[color:var(--muted-soft)]" title={chosen.downloadSha256}>
                    sha256 {chosen.downloadSha256}
                </span>
            ) : null}
        </>
    );
}

// ── Step 3: run it ───────────────────────────────────────────────

const PLATFORM_RUN_NOTE = {
    "PLATFORM_WINDOWS|INSTALLER_FORMAT_EXE":
        "The bundle elevates itself (one UAC prompt). The same command works under SYSTEM for Intune or SCCM.",
    "PLATFORM_WINDOWS|INSTALLER_FORMAT_MSI":
        "Run from an elevated prompt; msiexec doesn't self-elevate.",
};

// The macOS MDM path folded behind the run-note line: the note keeps
// carrying the one fact everyone needs (stage the token), and a quiet
// "MDM profiles" toggle expands the pre-approval download for the
// minority who deploy managed. Progressive disclosure, not another step.
function MdmProfilesDisclosure() {
    const [open, setOpen] = useState(false);
    const link = "w-max cursor-pointer border-0 bg-transparent p-0 text-[12.5px] leading-[1.4] text-[color:var(--muted)] underline decoration-[color:var(--line-strong)] underline-offset-2 transition-colors hover:text-[color:var(--ink)]";

    return (
        <div className="grid gap-[8px]">
            <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className={link}>
                {open ? "Hide MDM profiles" : "MDM profiles"}
            </button>
            {open ? (
                <>
                    <div className="flex flex-wrap items-center gap-[6px]">
                        {STATIC_MDM_PROFILES.map((p) => (
                            <Button key={p.filename} size="sm" onClick={() => downloadTextFile(p.filename, p.content)}>
                                {p.label}
                            </Button>
                        ))}
                    </div>
                    <a
                        href="https://support.originhq.com/docs/mdm-deployment-guides"
                        target="_blank"
                        rel="noreferrer"
                        className={link}
                    >
                        MDM deployment guides ↗
                    </a>
                </>
            ) : null}
        </div>
    );
}

export function CommandStep({ platform, chosen, jwt }) {
    // Keep the command useful when the catalog is unavailable or the role
    // cannot read it. The installer filename remains a deliberate placeholder
    // until an administrator supplies the actual file.
    const detectedPlatform = platform ?? detectInstallPlatform() ?? "PLATFORM_WINDOWS";
    const fallbackFormat = detectedPlatform === "PLATFORM_MACOS"
        ? "INSTALLER_FORMAT_PKG"
        : detectedPlatform === "PLATFORM_WINDOWS"
            ? "INSTALLER_FORMAT_EXE"
            : null;
    if (!chosen && !fallbackFormat) {
        return <p className={NOTE}>Launch the installer for this machine and paste the token during installation.</p>;
    }
    const commandPlatform = chosen ? platform : detectedPlatform;
    const command = installCommandFor({
        platform: commandPlatform,
        format: chosen?.installerFormat ?? fallbackFormat,
        version: chosen?.version,
        architecture: chosen?.architecture,
        jwt,
    });
    if (!command) {
        return (
            <p className={NOTE}>
                No packaged install command for this platform. Talk to your Origin contact about deployment.
            </p>
        );
    }
    const note = PLATFORM_RUN_NOTE[
        String(commandPlatform) + "|" + (chosen?.installerFormat ?? fallbackFormat)
    ];
    return (
        <>
            <CopyBlock label={`Command for ${PLATFORM_LABEL[commandPlatform] ?? "this machine"}`} value={command} highlight={jwt || null} />
            {note ? <p className={NOTE}>{note}</p> : null}
            {commandPlatform === "PLATFORM_MACOS" ? <MdmProfilesDisclosure /> : null}
        </>
    );
}

// ── The live strip ───────────────────────────────────────────────

function endpointSeenLabel(d) {
    if (!(d instanceof Date) || !Number.isFinite(d.getTime())) return "—";
    return `${dayLabelFormatter.format(d)} at ${timeLabelFormatter.format(d)}`;
}

export function endpointRowKey(row) {
    return row?.idRaw || row?.hostname || "";
}

/**
 * Merge a fresh first page into the live strip without dropping pages the
 * user already loaded. A fixed row slice is incorrect when an endpoint moves
 * between pages after a heartbeat refresh; only rows fetched by an explicit
 * "Load more" action are pinned across refreshes.
 */
export function mergeRecentEndpointPage(current, freshRows, total, nextCursor = null) {
    const freshKeys = new Set(freshRows.map(endpointRowKey));
    const pinnedKeys = current?.pinnedKeys ?? new Set();
    const preserved = (current?.rows ?? []).filter((row) => {
        const key = endpointRowKey(row);
        return pinnedKeys.has(key) && !freshKeys.has(key);
    }).slice(0, Math.max(Number(total) - freshRows.length, 0));
    return {
        rows: [...freshRows, ...preserved],
        total,
        // A refresh establishes a new page boundary. Keeping the old cursor
        // would page against a ranking that no longer exists; explicit null
        // must remain null after the end of the list.
        nextCursor,
        pinnedKeys,
    };
}

export function appendRecentEndpointPage(current, pageRows, total, nextCursor) {
    const existingKeys = new Set((current?.rows ?? []).map(endpointRowKey));
    const additions = pageRows.filter((row) => !existingKeys.has(endpointRowKey(row)));
    const pinnedKeys = new Set(current?.pinnedKeys ?? []);
    for (const row of additions) pinnedKeys.add(endpointRowKey(row));
    return {
        rows: [...(current?.rows ?? []), ...additions],
        total: Number(total ?? current?.total ?? 0),
        nextCursor: nextCursor ?? null,
        pinnedKeys,
    };
}

export function LiveStrip({ canReadEndpoints, canAnalytics, startedEmpty, onOpenFleet }) {
    // null = first fetch pending; { rows, total } thereafter.
    const [fleet, setFleet] = useState(null);
    const [fleetDenied, setFleetDenied] = useState(false);
    const [fleetError, setFleetError] = useState(false);
    const [activitySeen, setActivitySeen] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const hasEndpoints = (fleet?.total ?? 0) > 0;
    const canWatchFleet = canAnalytics || canReadEndpoints;
    const recentTotalRef = useRef(null);
    const lastTotalRefreshRef = useRef(0);

    // No visibility gating on the interval — the browser already throttles
    // hidden-tab timers, and skipping ticks outright left the strip stuck
    // on its initial state for a user who installed the agent with the
    // dashboard in a background window. The visibilitychange tick just
    // freshens the strip the moment they come back.
    const loadMoreGeneration = useRef(0);
    useEffect(() => () => { loadMoreGeneration.current += 1; }, []);

    useEffect(() => {
        if (!canWatchFleet || fleetDenied) return undefined;
        let cancelled = false;
        const tick = async () => {
            try {
                const includeTotal = canAnalytics && (
                    recentTotalRef.current === null
                    || Date.now() - lastTotalRefreshRef.current >= RECENT_TOTAL_REFRESH_MS
                );
                const resp = canAnalytics
                    ? await query(
                        "getRecentEndpointPage",
                        { limit: RECENT_LIMIT, includeTotal },
                        { cache: false },
                    )
                    : await restGet("/v1/endpoints/metadata", { page: 0, page_size: RECENT_LIMIT });
                if (cancelled) return;
                const rows = canAnalytics
                    ? resp?.rows ?? []
                    : (resp?.metadata ?? []).map(flattenInventoryRow);
                const fetchedTotal = resp?.total == null ? null : Number(resp.total);
                if (fetchedTotal !== null) {
                    recentTotalRef.current = fetchedTotal;
                    lastTotalRefreshRef.current = Date.now();
                }
                const total = fetchedTotal ?? Math.max(recentTotalRef.current ?? 0, rows.length);
                setFleet((current) => mergeRecentEndpointPage(
                    current,
                    rows,
                    total,
                    canAnalytics ? resp?.nextCursor ?? null : null,
                ));
                setFleetError(false);
                if (total > 0) markTenantConnected(total);
            } catch (err) {
                if (cancelled) return;
                // A denial is terminal (the role can't read the fleet); a
                // network blip keeps the interval so the strip self-heals.
                if (err?.status === 403) setFleetDenied(true);
                else setFleetError(true);
            }
        };
        tick();
        const id = window.setInterval(tick, hasEndpoints ? CHECKIN_POLL_SLOW_MS : CHECKIN_POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible") tick();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            cancelled = true;
            window.clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [canAnalytics, canReadEndpoints, canWatchFleet, fleetDenied, hasEndpoints]);

    const loadMore = useCallback(async () => {
        if (!fleet || loadingMore || fleet.rows.length >= fleet.total || !fleet.nextCursor) return;
        setLoadingMore(true);
        const generation = loadMoreGeneration.current;
        try {
            const resp = await query(
                "getRecentEndpointPage",
                { cursor: fleet.nextCursor, limit: RECENT_LIMIT, includeTotal: false },
                { cache: false },
            );
            if (generation !== loadMoreGeneration.current) return;
            setFleet((current) => appendRecentEndpointPage(
                current,
                resp?.rows ?? [],
                resp?.total,
                resp?.nextCursor,
            ));
        } catch {
            if (generation === loadMoreGeneration.current) setFleetError(true);
        } finally {
            if (generation === loadMoreGeneration.current) setLoadingMore(false);
        }
    }, [fleet, loadingMore]);

    // Second phase of the arc, only for a session that STARTED empty: probe
    // for the first prompt-bearing endpoint via the analytics graph (the
    // same population the Explore sidebar counts). A tenant that was already
    // connected skips it — "first activity" means nothing to them.
    useEffect(() => {
        if (!startedEmpty || !canAnalytics || !hasEndpoints || activitySeen) return undefined;
        let cancelled = false;
        const tick = async () => {
            try {
                const resp = await query(
                    "getDimensionEntries",
                    { dimKey: "endpoints", parentScopePath: [], limit: 1, sparkline: false, minimal: true },
                    { cache: false },
                );
                if (cancelled) return;
                if ((resp?.total ?? 0) > 0) setActivitySeen(true);
            } catch {
                // Best-effort probe; the next tick retries.
            }
        };
        tick();
        const id = window.setInterval(tick, ACTIVITY_POLL_MS);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [startedEmpty, canAnalytics, hasEndpoints, activitySeen]);

    if (!canWatchFleet) {
        return (
            <Notice title="Check-ins show in Fleet">
                Ask an administrator to confirm that the endpoint arrived.
            </Notice>
        );
    }
    if (fleetDenied) {
        return (
            <Notice title="Check-ins show in Fleet">
                Your role can't query endpoint activity. Ask an administrator to confirm the endpoint arrived.
            </Notice>
        );
    }

    return (
        <div
            aria-busy={fleet === null ? "true" : undefined}
            aria-live="polite"
            className="grid min-w-0 gap-[10px]"
        >
            {fleet === null ? (
                <>
                    <p className={STATUS}>Checking the fleet…</p>
                    <ShimmerBar />
                    {fleetError ? <p className={NOTE}>Connection hiccup. Retrying.</p> : null}
                </>
            ) : !hasEndpoints ? (
                <>
                    <p className={STATUS}>Listening for the first check-in</p>
                    <ShimmerBar />
                    <p className={NOTE}>
                        Endpoints register and report within about a minute of the installer finishing. This page updates itself.
                        {fleetError ? " Connection hiccup. Retrying." : ""}
                    </p>
                </>
            ) : (
                <>
                    <p className={`${STATUS} flex items-center gap-[8px]`}>
                        <span aria-hidden="true" className="h-[7px] w-[7px] shrink-0 rounded-full bg-[color:var(--green)]" />
                        {canAnalytics
                            ? (fleet.total === 1 ? "1 endpoint reporting in the last 30 days" : `${fleet.total} endpoints reporting in the last 30 days`)
                            : (fleet.total === 1 ? "1 endpoint connected" : `${fleet.total} endpoints connected`)}
                    </p>
                    <div className="grid border-b border-[color:var(--line-soft)]">
                        {fleet.rows.map((r) => (
                            <div
                                key={r.idRaw || r.hostname}
                                className="page-enter grid items-center gap-x-[12px] border-t border-[color:var(--line-soft)] px-[6px] py-[7px] text-[12.5px] leading-[1.2] [grid-template-columns:minmax(120px,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_auto]"
                            >
                                <span className="truncate text-[color:var(--ink)]">{r.hostname || "—"}</span>
                                <span className="truncate text-[color:var(--muted)]">{r.platform || "—"}</span>
                                <span className="truncate text-[color:var(--muted)]">
                                    {r.agentVersion ? `Agent ${r.agentVersion}` : "—"}
                                </span>
                                <span className="text-[color:var(--muted-soft)]">{endpointSeenLabel(r.snapshotTime)}</span>
                            </div>
                        ))}
                    </div>
                    {canAnalytics && fleet.nextCursor && fleet.rows.length < fleet.total ? (
                        <div className="flex flex-wrap items-center justify-between gap-[8px]">
                            <p className={NOTE}>Showing {fleet.rows.length} of {fleet.total} recent endpoints.</p>
                            <Button size="sm" onClick={loadMore} disabled={loadingMore}>
                                {loadingMore ? "Loading…" : "Load more endpoints"}
                            </Button>
                        </div>
                    ) : null}
                    {startedEmpty && canAnalytics && !activitySeen ? (
                        <div className="grid gap-[8px] pt-[6px]">
                            <p className={STATUS}>Waiting for the first AI activity</p>
                            <ShimmerBar />
                            <p className={NOTE}>
                                Use an AI tool on that machine: an editor copilot, a chat app, a CLI agent. Its first prompt lands here.
                            </p>
                        </div>
                    ) : null}
                    {startedEmpty && activitySeen ? (
                        <Notice
                            tone="ok"
                            title="First AI activity captured"
                            className="page-enter"
                            action={onOpenFleet ? <Button variant="primary" size="sm" onClick={onOpenFleet}>Open Fleet</Button> : null}
                        >
                            Your organization's AI usage is on the record from here on.
                        </Notice>
                    ) : null}
                </>
            )}
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────

// Enrolled endpoints, in Settings' Endpoints group: the live strip of
// check-ins and first AI activity, newest first.
export function EnrolledEndpoints({ onScopeNavigate = null }) {
    const caps = useMyPermissions();
    const connect = useConnectState();
    // "Started empty" pins the arc: the completion treatment only belongs to
    // the session that watched its first endpoint arrive. Latched from the
    // first confirmed reading so the strip doesn't lose its arc the moment
    // the endpoint checks in and `connected` flips.
    const startedEmptyRef = useRef(null);
    if (startedEmptyRef.current === null && connect.status === "ready") {
        startedEmptyRef.current = connect.connected === false;
    }
    return (
        <LiveStrip
            canReadEndpoints={hasPermission(caps, PERMISSIONS.ENDPOINT_METADATA_READ)}
            canAnalytics={hasPermission(caps, PERMISSIONS.ANALYTICS_EXECUTE)}
            startedEmpty={startedEmptyRef.current === true}
            onOpenFleet={onScopeNavigate ? () => onScopeNavigate([]) : null}
        />
    );
}

// `checkIns` (the enrolled-endpoints strip) is the fourth step when given.
export default function ConnectPage({ checkIns = null }) {
    const caps = useMyPermissions();
    const canManage = hasPermission(caps, PERMISSIONS.PROVISIONING_TOKENS_MANAGE);
    const canInstallers = hasPermission(caps, PERMISSIONS.INSTALLERS_READ);

    const [jwt, setJwt] = useState(_sessionJwt);
    const adoptJwt = useCallback((token) => {
        _sessionJwt = token;
        setJwt(token);
    }, []);

    // Installer catalog — one shot; the artifact list doesn't change under
    // the user mid-mission. { status, rows } with rows pre-reduced to the
    // newest build per environment and universal-collapsed.
    const [installers, setInstallers] = useState({ status: canInstallers ? "loading" : "denied", rows: [] });
    useEffect(() => {
        if (!canInstallers) {
            setInstallers({ status: "denied", rows: [] });
            return undefined;
        }
        let cancelled = false;
        setInstallers({ status: "loading", rows: [] });
        restGet("/v1/installers", { page: 0, page_size: 1000 })
            .then((resp) => {
                if (cancelled) return;
                const flat = (resp?.installers ?? []).map(flattenInstaller);
                const rows = collapseUniversalRows(latestPerEnvironment(flat));
                setInstallers({ status: "ready", rows });
            })
            .catch((err) => {
                if (cancelled) return;
                setInstallers({ status: err?.status === 403 ? "denied" : "error", rows: [] });
            });
        return () => { cancelled = true; };
    }, [canInstallers]);

    const [installerChoice, setInstallerChoice] = useState(null);
    const installerChoices = useMemo(() => installerChoicesForRows(installers.rows).map((choice) => ({
        ...choice,
        installer: pickInstaller(installers.rows, choice.platform, choice.format),
    })), [installers.rows]);
    useEffect(() => {
        if (!installerChoices.length) {
            setInstallerChoice(null);
            return;
        }
        if (installerChoices.some((choice) => choice.key === installerChoice && choice.installer)) return;
        const detected = detectInstallPlatform();
        const preferred = installerChoices.find((choice) => choice.platform === detected && choice.installer)
            ?? installerChoices.find((choice) => choice.installer);
        setInstallerChoice(preferred?.key ?? null);
    }, [installerChoice, installerChoices]);
    const selectedChoice = installerChoices.find((choice) => choice.key === installerChoice) ?? null;
    const platform = selectedChoice?.platform ?? null;
    const chosen = selectedChoice?.installer ?? null;

    return (
        <Steps label="Enroll an endpoint" className="min-w-0">
            <Step number={1} title="Token" state={jwt ? "done" : "current"} data-connect-step={1}>
                <TokenStep jwt={jwt} onJwt={adoptJwt} canManage={canManage} caps={caps} />
            </Step>
            <Step number={2} title="Installer" state={jwt ? "current" : "todo"} data-connect-step={2}>
                <InstallerStep
                    installers={installers}
                    choiceKey={installerChoice}
                    onChoice={setInstallerChoice}
                    choices={installerChoices}
                    chosen={chosen}
                />
            </Step>
            <Step
                number={3}
                title="Install command"
                state="todo"
                collapsed={!jwt}
                last={!checkIns}
                data-connect-step={3}
                data-collapsed={jwt ? undefined : ""}
            >
                <CommandStep platform={platform} chosen={chosen} jwt={jwt} />
            </Step>
            {checkIns ? (
                <Step number={4} title="Check in" state="todo" last data-connect-step={4}>
                    {checkIns}
                </Step>
            ) : null}
        </Steps>
    );
}
